/**
 * registrations/billing/ContactsPage.tsx
 *
 * Extends the base registrations/ContactsPage with billing-specific features:
 *  - Combobox filter: receivable / payable
 *  - Toggle chips: Vencidos / Vence em 7 dias
 *  - Score badge on cards/table
 *  - Actions: Ficha Financeira, Contas e PendÃªncias, CobranÃ§a WhatsApp, Aviso de Vencimento
 *  - Modals: FinancialProfileModal, PendenciesModal, WhatsAppTemplateModal, QuickChargeModal
 */
import { useState, useCallback, ReactNode } from 'react'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { useChargeTemplate } from '@/modules/billing/entries/useChargeTemplate'
import { useDueDateTemplate } from '@/modules/billing/entries/useDueDateTemplate'
import { Modal } from '@/shared/components/ui'
import { type DataTableColumn } from '@/shared/components/entities'
import { WhatsAppTemplateModal } from '@/shared/components/messaging/WhatsAppTemplateModal'
import { QuickChargeModal } from '@/modules/billing/entries/QuickChargeModal'
import { PartyDocumentModal } from '@/modules/registrations/contacts/PartyDocumentModal'
import { WhatsAppHistoryBadge } from '@/modules/billing/entries/WhatsAppHistoryBadge'
import { WhatsAppHistoryBadgeParty } from '@/modules/billing/entries/WhatsAppHistoryBadgeParty'
import type { ContactType, PartyWithStats } from '@/modules/registrations/contacts/contactsTypes'
import {
  listEntriesForParty,
  isOverdue,
} from '@/modules/billing/entries/entriesService'
import type { FinancialEntry } from '@/modules/billing/entries/entriesTypes'
import { logWhatsAppMessage } from '@/modules/billing/entries/whatsappMessagesService'
import { openWhatsAppWindow } from '@/lib/integrations/whatsapp'
import {
  ContactsPage as BaseContactsPage,
  type ContactsPageExtensions,
  type ContactsPageContext,
  formatCurrency,
  buildWhatsAppUrl,
  TypeChip,
  ICON_BG_STYLES,
  WhatsAppIcon,
} from '@/modules/registrations/contacts/ContactsPage'

// â”€â”€â”€ Score helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const SCORE_STYLES: Record<string, string> = {
  A: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  B: 'bg-green-100   text-green-700   dark:bg-green-900/30   dark:text-green-400',
  C: 'bg-amber-100   text-amber-700   dark:bg-amber-900/30   dark:text-amber-400',
  D: 'bg-orange-100  text-orange-700  dark:bg-orange-900/30  dark:text-orange-400',
  E: 'bg-rose-100    text-rose-700    dark:bg-rose-900/30    dark:text-rose-400',
}

function ScoreChip({ score }: { score: PartyWithStats['score'] }) {
  if (!score) return <span className="text-slate-400 text-xs">â€”</span>
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${SCORE_STYLES[score]}`}>
      {score}
    </span>
  )
}

// â”€â”€â”€ Financial Profile Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function FinancialProfileModal({
  party, onClose, typeLabels, scoreLabels, profileLabels, currentLocale,
}: {
  party:         PartyWithStats | null
  onClose:       () => void
  typeLabels:    Record<string, string>
  scoreLabels:   Record<string, string>
  profileLabels: Record<string, string>
  currentLocale: string
}) {
  if (!party) return null

  const StatCard = ({
    title, count, total, emptyLabel, danger,
  }: { title: string; count: number; total: number; emptyLabel: string; danger: boolean }) => (
    <div className={`rounded-lg p-3 border ${
      danger
        ? 'border-rose-200 bg-rose-50 dark:border-rose-800/50 dark:bg-rose-900/20'
        : 'border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-900/20'
    }`}>
      <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${
        danger ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'
      }`}>{title}</div>
      {count === 0
        ? <p className="text-sm text-slate-500 dark:text-slate-400">{emptyLabel}</p>
        : <>
          <div className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">
            {formatCurrency(total, currentLocale)}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {profileLabels.count.replace('{{count}}', String(count))}
          </div>
        </>
      }
    </div>
  )

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={profileLabels.title.replace('{{name}}', party.name)}
      size="md"
      headerRight={(
        <div className="hidden md:flex items-center gap-2">
          <button onClick={onClose}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
            title="Fechar" aria-label="Fechar">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 dark:text-slate-400 min-w-[80px]">{typeLabels.label}:</span>
          <TypeChip type={party.party_type} labels={typeLabels} />
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
            {profileLabels.scoreSectionTitle}
          </div>
          {party.score === null
            ? <p className="text-sm text-slate-400">{profileLabels.noPaidHistory}</p>
            : (
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center justify-center h-10 w-10 rounded-full text-xl font-extrabold ${SCORE_STYLES[party.score]}`}>
                  {party.score}
                </span>
                <div>
                  <div className="text-sm font-semibold text-slate-900 dark:text-white">{scoreLabels[party.score]}</div>
                  {party.score_pct !== null && (
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {scoreLabels.pct.replace('{{pct}}', String(party.score_pct))}
                    </div>
                  )}
                </div>
              </div>
            )
          }
        </div>
        <div className="grid grid-cols-2 gap-3">
          <StatCard title={profileLabels.overdueSection} count={party.overdue_count} total={party.overdue_total} emptyLabel={profileLabels.noOverdue} danger />
          <StatCard title={profileLabels.openSection}   count={party.open_count}    total={party.open_total}    emptyLabel={profileLabels.noOpen}    danger={false} />
        </div>
        {(party.first_paid_at || party.last_paid_at) && (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {profileLabels.datesSection}
            </div>
            {party.first_paid_at && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">{profileLabels.firstPaid}</span>
                <span className="font-medium text-slate-900 dark:text-white tabular-nums">
                  {formatDate(party.first_paid_at)}
                </span>
              </div>
            )}
            {party.last_paid_at && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">{profileLabels.lastPaid}</span>
                <span className="font-medium text-slate-900 dark:text-white tabular-nums">
                  {formatDate(party.last_paid_at)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

// â”€â”€â”€ Pendencies Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function PendenciesModal({
  party, entries, loading, onClose, labels, currentLocale,
}: {
  party:         PartyWithStats | null
  entries:       FinancialEntry[]
  loading:       boolean
  onClose:       () => void
  labels:        { title: string; receivable: string; payable: string; empty: string }
  currentLocale: string
}) {
  if (!party) return null

  const receivable = entries.filter(e => e.entry_type === 'receivable')
  const payable    = entries.filter(e => e.entry_type === 'payable')

  const EntryRow = ({ entry }: { entry: FinancialEntry }) => {
    const overdue = isOverdue(entry)
    return (
      <div className="flex items-start justify-between py-3 border-b border-slate-100 dark:border-slate-700 last:border-0">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900 dark:text-white truncate">{entry.description}</div>
          <div className={`text-xs ${overdue ? 'text-rose-500' : 'text-slate-500 dark:text-slate-400'}`}>
            {overdue ? 'âš  ' : ''}{formatDate(entry.due_date)}
          </div>
          <div className="mt-1"><WhatsAppHistoryBadge entryId={entry.id} /></div>
        </div>
        <div className="ml-3 text-right flex-shrink-0">
          <div className="text-sm font-semibold tabular-nums text-slate-900 dark:text-white">
            {formatCurrency(entry.amount, currentLocale)}
          </div>
          <div className={`text-xs ${
            entry.status === 'paid' ? 'text-emerald-600 dark:text-emerald-400'
            : overdue               ? 'text-rose-500'
            :                         'text-amber-500'
          }`}>
            {entry.status === 'paid' ? 'Pago' : overdue ? 'Vencida' : 'Em aberto'}
          </div>
        </div>
      </div>
    )
  }

  const Section = ({ title, items }: { title: string; items: FinancialEntry[] }) => {
    if (items.length === 0) return null
    const total = items.reduce((s, e) => s + e.amount, 0)
    return (
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h4>
          <span className="text-xs font-semibold tabular-nums text-slate-700 dark:text-slate-300">
            {formatCurrency(total, currentLocale)}
          </span>
        </div>
        {items.map(e => <EntryRow key={e.id} entry={e} />)}
      </div>
    )
  }

  return (
    <Modal isOpen onClose={onClose} title={labels.title.replace('{{name}}', party.name)} size="md"
      headerRight={(
        <div className="hidden md:flex items-center gap-2">
          <button onClick={onClose}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
            title="Fechar" aria-label="Fechar">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
        </div>
      ) : entries.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-8">{labels.empty}</p>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto">
          <Section title={labels.receivable} items={receivable} />
          <Section title={labels.payable}    items={payable} />
        </div>
      )}
    </Modal>
  )
}

// â”€â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function formatDate(dateStr: string, locale: string = 'pt-BR') {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(locale)
}

// â”€â”€â”€ Billing ContactsPage â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function ContactsPage() {
  const { t, locale } = useTranslation()
  const { currentUnit } = useUnit()

  const { template: chargeTemplateStored,  saveTemplate: saveChargeTemplate,  resetTemplate: resetChargeTemplate,  isLoaded: chargeTemplateLoaded  } = useChargeTemplate()
  const { template: dueDateTemplateStored, saveTemplate: saveDueDateTemplate, resetTemplate: resetDueDateTemplate, isLoaded: dueDateTemplateLoaded } = useDueDateTemplate()

  const currentLocale = locale
  const unitId        = currentUnit?.id   ?? ''

  const tc = useCallback(
    (key: string, params?: Record<string, string>) => t(`billing.contacts.${key}`, params),
    [t],
  )

  // â”€â”€ Financial type filter (receivable / payable / all / inactive) â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const [typeFilter,    setTypeFilter]    = useState<'all' | 'receivable' | 'payable' | 'inactive'>('all')
  const [filterOverdue, setFilterOverdue] = useState(false)
  const [filterDueIn7,  setFilterDueIn7]  = useState(false)

  // â”€â”€ Financial modals state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const [profileParty,           setProfileParty]           = useState<PartyWithStats | null>(null)
  const [pendingParty,           setPendingParty]           = useState<PartyWithStats | null>(null)
  const [pendingEntries,         setPendingEntries]         = useState<FinancialEntry[]>([])
  const [loadingEntries,         setLoadingEntries]         = useState(false)
  const [chargeTemplate,         setChargeTemplate]         = useState<PartyWithStats | null>(null)
  const [quickChargeParty,       setQuickChargeParty]       = useState<PartyWithStats | null>(null)
  const [editingTemplate,        setEditingTemplate]        = useState(false)
  const [dueDateTemplate,        setDueDateTemplate]        = useState<PartyWithStats | null>(null)
  const [quickDueDateParty,      setQuickDueDateParty]      = useState<PartyWithStats | null>(null)
  const [editingDueDateTemplate, setEditingDueDateTemplate] = useState(false)
  const [documentParty,          setDocumentParty]          = useState<PartyWithStats | null>(null)

  // â”€â”€ Label objects â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const typeLabels: Record<string, string> = {
    label:    'Tipo',
    customer: tc('contactType.customer'),
    supplier: tc('contactType.supplier'),
    both:     tc('contactType.both'),
    none:     tc('contactType.none'),
  }

  const scoreLabels: Record<string, string> = {
    A: tc('score.A'), B: tc('score.B'), C: tc('score.C'), D: tc('score.D'), E: tc('score.E'),
    pct: tc('score.pct', { pct: '{{pct}}' }),
  }

  const profileLabels: Record<string, string> = {
    title:             tc('financialProfile.title',             { name:   '{{name}}'   }),
    scoreSectionTitle: tc('financialProfile.scoreSectionTitle'),
    noPaidHistory:     tc('financialProfile.noPaidHistory'),
    overdueSection:    tc('financialProfile.overdueSection'),
    openSection:       tc('financialProfile.openSection'),
    datesSection:      tc('financialProfile.datesSection'),
    firstPaid:         tc('financialProfile.firstPaid'),
    lastPaid:          tc('financialProfile.lastPaid'),
    count:             tc('financialProfile.count',             { count:  '{{count}}'  }),
    total:             tc('financialProfile.total',             { amount: '{{amount}}' }),
    noOverdue:         tc('financialProfile.noOverdue'),
    noOpen:            tc('financialProfile.noOpen'),
  }

  // â”€â”€ Pendencies handler â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const handleOpenPendencies = useCallback(async (party: PartyWithStats) => {
    setPendingParty(party)
    setPendingEntries([])
    setLoadingEntries(true)
    try {
      const entries = await listEntriesForParty(unitId, party.id)
      setPendingEntries(entries)
    } catch { /* silently ignore */ }
    finally { setLoadingEntries(false) }
  }, [unitId])

  // â”€â”€ WhatsApp helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const openChargeWhatsApp = useCallback((party: PartyWithStats) => {
    if (!party.whatsapp) { alert(tc('messages.noWhatsApp')); return }
    if (party.overdue_count === 0) { alert(tc('messages.noOverdue')); return }
    setQuickChargeParty(party)
  }, [tc])

  const openDueDateWhatsApp = useCallback((party: PartyWithStats) => {
    if (!party.whatsapp) { alert(tc('messages.noWhatsApp')); return }
    if (party.open_count === 0) return
    setQuickDueDateParty(party)
  }, [tc])

  const renderMessage = (party: PartyWithStats) =>
    chargeTemplateStored
      .replace(/\{\{name\}\}/g,   party.name)
      .replace(/\{\{count\}\}/g,  String(party.overdue_count))
      .replace(/\{\{amount\}\}/g, formatCurrency(party.overdue_total, currentLocale))
      .replace(/\{\{unit\}\}/g,   '')

  const renderDueDateMessage = (party: PartyWithStats) =>
    dueDateTemplateStored
      .replace(/\{\{name\}\}/g,   party.name)
      .replace(/\{\{count\}\}/g,  String(party.due_in_7_days_count))
      .replace(/\{\{amount\}\}/g, formatCurrency(party.due_in_7_days_total, currentLocale))
      .replace(/\{\{unit\}\}/g,   '')

  // â”€â”€ Extensions object â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const extensions: ContactsPageExtensions = {

    // â”€â”€ Chips: adds receivable/payable options + overdue/7-day toggles â”€â”€â”€â”€â”€â”€

    chipsLeading: (_ctx: ContactsPageContext, _baseChips: ReactNode) => (
      <div className="flex items-center gap-2">
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as any)}
          className="text-sm rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1 min-w-[180px] text-slate-900 dark:text-white"
        >
          <option value="all">{tc('filters.presets.all')}</option>
          <option value="receivable">{tc('filters.presets.contactsReceivable')}</option>
          <option value="payable">{tc('filters.presets.contactsPayable')}</option>
          <option value="inactive">{tc('filters.presets.contactsInactive')}</option>
        </select>
        <button
          onClick={() => setFilterOverdue(v => !v)}
          className={`shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterOverdue ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500'}`}
        >
          {tc('filters.presets.overdue')}
        </button>
        <button
          onClick={() => setFilterDueIn7(v => !v)}
          className={`shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterDueIn7 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500'}`}
        >
          {tc('filters.presets.dueIn7Days')}
        </button>
      </div>
    ),

    // â”€â”€ Selectable: inactive allowed when typeFilter=inactive â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    isEntitySelectable: (party) => party.is_active || typeFilter === 'inactive',

    // â”€â”€ applyFilters: adds receivable/payable/overdue/7-day predicates â”€â”€â”€â”€â”€â”€

    applyFilters: (parties, query, _filterValues, _base) => {
      let results = parties

      if (query && query.trim()) {
        const q = query.trim().toLowerCase()
        results = results.filter((p: PartyWithStats) =>
          p.name.toLowerCase().includes(q) ||
          (p.whatsapp ?? '').includes(q)   ||
          (p.pix_key  ?? '').toLowerCase().includes(q) ||
          (p.notes    ?? '').toLowerCase().includes(q),
        )
      }

      if      (typeFilter === 'inactive')   results = results.filter(p => !p.is_active)
      else if (typeFilter === 'receivable') results = results.filter(p => p.is_active && (p.receivable_total ?? 0) > 0)
      else if (typeFilter === 'payable')    results = results.filter(p => p.is_active && (p.payable_total    ?? 0) > 0)
      else                                  results = results.filter(p => p.is_active)

      if (filterOverdue) {
        if      (typeFilter === 'receivable') results = results.filter(p => (p.overdue_receivable_total ?? 0) > 0)
        else if (typeFilter === 'payable')    results = results.filter(p => (p.overdue_payable_total    ?? 0) > 0)
        else                                  results = results.filter(p => (p.overdue_total            ?? 0) > 0)
      }

      if (filterDueIn7) {
        if      (typeFilter === 'receivable') results = results.filter(p => (p.due_in_7_days_receivable_total ?? 0) > 0)
        else if (typeFilter === 'payable')    results = results.filter(p => (p.due_in_7_days_payable_total    ?? 0) > 0)
        else                                  results = results.filter(p => (p.due_in_7_days_total            ?? 0) > 0)
      }

      return results
    },

    // â”€â”€ Extra table columns: score + overdue + 7-day â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    extraTableColumns: (_ctx): DataTableColumn<PartyWithStats>[] => [
      {
        key:   'score',
        label: tc('list.columns.score'),
        render: (p) => <ScoreChip score={p.score} />,
      },
      {
        key:   'due_in_7_days',
        label: tc('card.dueIn7DaysLabel'),
        render: (p) => p.due_in_7_days_count > 0 ? (
          <div>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              {p.due_in_7_days_count}Ã—
            </span>
            <div className="text-xs text-amber-500 tabular-nums mt-0.5">
              {formatCurrency(p.due_in_7_days_total, currentLocale)}
            </div>
          </div>
        ) : <span className="text-slate-400 text-xs">â€”</span>,
      },
      {
        key:   'overdue',
        label: tc('list.columns.overdue'),
        render: (p) => p.overdue_count > 0 ? (
          <div>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
              {p.overdue_count}Ã—
            </span>
            <div className="text-xs text-rose-500 tabular-nums mt-0.5">
              {formatCurrency(p.overdue_total, currentLocale)}
            </div>
          </div>
        ) : <span className="text-slate-400 text-xs">â€”</span>,
      },
      {
        key:   'whatsapp_notifications',
        label: tc('whatsappNotifications.columnLabel'),
        render: (p) => <WhatsAppHistoryBadgeParty partyId={p.id} unitId={unitId} />,
      },
    ],

    // â”€â”€ Card badge: score â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    getCardBadge: (p) => {
      if (!p.score) return null
      const variants: Record<string, 'success' | 'warning' | 'danger'> = {
        A: 'success', B: 'success', C: 'warning', D: 'warning', E: 'danger',
      }
      const labels: Record<string, string> = {
        A: `A â€” ${tc('score.A')}`, B: `B â€” ${tc('score.B')}`,
        C: `C â€” ${tc('score.C')}`, D: `D â€” ${tc('score.D')}`,
        E: `E â€” ${tc('score.E')}`,
      }
      return { label: labels[p.score], variant: variants[p.score] }
    },

    // â”€â”€ Extra card/grid actions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    extraActions: (p) => {
      if (!p.is_active) return []
      const actions: any[] = []

      if (p.whatsapp && p.overdue_count > 0) {
        actions.push({
          label:   tc('actions.chargeWhatsApp'),
          icon:    <WhatsAppIcon className="h-4 w-4" />,
          onClick: () => openChargeWhatsApp(p),
          variant: 'success' as const,
        })
      }
      if (p.whatsapp && p.due_in_7_days_count > 0) {
        actions.push({
          label:   tc('actions.dueDateAlert'),
          icon:    <BellIcon />,
          onClick: () => openDueDateWhatsApp(p),
          variant: 'warning' as const,
        })
      }
      actions.push({
        label:   tc('actions.financialProfile'),
        icon:    <ChartIcon />,
        onClick: () => setProfileParty(p),
        variant: 'info' as const,
      })
      actions.push({
        label:   tc('actions.pendencies'),
        icon:    <ClipboardIcon />,
        onClick: () => handleOpenPendencies(p),
        variant: 'info' as const,
      })

      return actions
    },

    // â”€â”€ Extra row-dropdown actions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    extraRowActions: (p) => {
      if (!p.is_active) return []
      const actions: any[] = []

      if (p.whatsapp && p.overdue_count > 0) {
        actions.push({
          label:   tc('actions.chargeWhatsApp'),
          icon:    <WhatsAppIcon className="h-4 w-4" />,
          onClick: () => openChargeWhatsApp(p),
        })
      }
      if (p.whatsapp && p.due_in_7_days_count > 0) {
        actions.push({
          label:   tc('actions.dueDateAlert'),
          icon:    <BellIcon />,
          onClick: () => openDueDateWhatsApp(p),
        })
      }
      actions.push({
        label:   tc('actions.financialProfile'),
        icon:    <ChartIcon />,
        onClick: () => setProfileParty(p),
      })
      actions.push({
        label:   tc('actions.pendencies'),
        icon:    <ClipboardIcon />,
        onClick: () => handleOpenPendencies(p),
      })

      return actions
    },

    // â”€â”€ Row quick action: charge WhatsApp â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    rowQuickAction: (p) =>
      p.whatsapp && p.overdue_count > 0
        ? { icon: <WhatsAppIcon className="h-4 w-4" />, label: tc('actions.chargeWhatsApp'), onClick: () => openChargeWhatsApp(p) }
        : null,

    // â”€â”€ Extra modals â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

    extraModals: (_ctx) => (
      <>
        <FinancialProfileModal
          party={profileParty}
          onClose={() => setProfileParty(null)}
          typeLabels={typeLabels}
          scoreLabels={scoreLabels}
          profileLabels={profileLabels}
          currentLocale={currentLocale}
        />

        <PendenciesModal
          party={pendingParty}
          entries={pendingEntries}
          loading={loadingEntries}
          onClose={() => setPendingParty(null)}
          labels={{
            title:      tc('pendencies.title'),
            receivable: tc('pendencies.receivable'),
            payable:    tc('pendencies.payable'),
            empty:      tc('pendencies.empty'),
          }}
          currentLocale={currentLocale}
        />

        {/* Charge template editor */}
        <WhatsAppTemplateModal
          isOpen={editingTemplate && !!chargeTemplate}
          onClose={() => { setEditingTemplate(false); setChargeTemplate(null) }}
          initialTemplate={chargeTemplateStored}
          availableFields={[
            { key: 'name',   label: tc('templateModal.fields.name'),          type: 'text',   value: chargeTemplate?.name ?? '' },
            { key: 'count',  label: tc('templateModal.fields.count_overdue'), type: 'number', value: String(chargeTemplate?.overdue_count ?? 0) },
            { key: 'amount', label: tc('templateModal.fields.amount'),        type: 'number', value: chargeTemplate?.overdue_total ? formatCurrency(chargeTemplate.overdue_total, currentLocale) : '' },
          ]}
          onSave={(finalMessage: string) => {
            if (chargeTemplate?.whatsapp) {
              saveChargeTemplate(finalMessage)
              const rendered = finalMessage
                .replace(/\{\{name\}\}/g,   chargeTemplate.name)
                .replace(/\{\{count\}\}/g,  String(chargeTemplate.overdue_count))
                .replace(/\{\{amount\}\}/g, formatCurrency(chargeTemplate.overdue_total, currentLocale))
                .replace(/\{\{unit\}\}/g,   '')
              openWhatsAppWindow(buildWhatsAppUrl(chargeTemplate.whatsapp, rendered))
              ;(async () => {
                try {
                  const entries = await listEntriesForParty(unitId, chargeTemplate.id)
                  for (const entry of entries.filter(e => isOverdue(e))) {
                    await logWhatsAppMessage({ party_id: chargeTemplate.id, entry_id: entry.id, unit_id: unitId, type: 'charge', message_content: finalMessage, phone: chargeTemplate.whatsapp! })
                  }
                } catch (err) { console.error('Failed to log WhatsApp message:', err) }
              })()
              setChargeTemplate(null); setEditingTemplate(false); setQuickChargeParty(null)
            }
          }}
          title={tc('templateModal.titleCharge')}
        />

        {/* Quick charge preview */}
        <QuickChargeModal
          isOpen={!!quickChargeParty && !editingTemplate && chargeTemplateLoaded}
          onClose={() => { setQuickChargeParty(null); setChargeTemplate(null) }}
          party={quickChargeParty ? { name: quickChargeParty.name, whatsapp: quickChargeParty.whatsapp ?? '' } : null}
          message={quickChargeParty ? renderMessage(quickChargeParty) : ''}
          onConfigure={() => { setChargeTemplate(quickChargeParty); setEditingTemplate(true) }}
          onResetTemplate={() => resetChargeTemplate()}
          onSend={() => {
            if (quickChargeParty) {
              openWhatsAppWindow(buildWhatsAppUrl(quickChargeParty.whatsapp!, renderMessage(quickChargeParty)))
              ;(async () => {
                try {
                  const entries = await listEntriesForParty(unitId, quickChargeParty.id)
                  for (const entry of entries.filter(e => isOverdue(e))) {
                    await logWhatsAppMessage({ party_id: quickChargeParty.id, entry_id: entry.id, unit_id: unitId, type: 'charge', message_content: chargeTemplateStored, phone: quickChargeParty.whatsapp! })
                  }
                } catch (err) { console.error('Failed to log WhatsApp message:', err) }
              })()
              setQuickChargeParty(null); setChargeTemplate(null)
            }
          }}
        />

        {/* Due date template editor */}
        <WhatsAppTemplateModal
          isOpen={editingDueDateTemplate && !!dueDateTemplate}
          onClose={() => { setEditingDueDateTemplate(false); setDueDateTemplate(null) }}
          initialTemplate={dueDateTemplateStored}
          availableFields={[
            { key: 'name',   label: tc('templateModal.fields.name'),           type: 'text',   value: dueDateTemplate?.name ?? '' },
            { key: 'count',  label: tc('templateModal.fields.count_due_soon'), type: 'number', value: String(dueDateTemplate?.due_in_7_days_count ?? 0) },
            { key: 'amount', label: tc('templateModal.fields.amount'),         type: 'number', value: dueDateTemplate?.due_in_7_days_total ? formatCurrency(dueDateTemplate.due_in_7_days_total, currentLocale) : '' },
          ]}
          onSave={(finalMessage: string) => {
            if (dueDateTemplate?.whatsapp) {
              saveDueDateTemplate(finalMessage)
              const rendered = finalMessage
                .replace(/\{\{name\}\}/g,   dueDateTemplate.name)
                .replace(/\{\{count\}\}/g,  String(dueDateTemplate.due_in_7_days_count))
                .replace(/\{\{amount\}\}/g, formatCurrency(dueDateTemplate.due_in_7_days_total, currentLocale))
                .replace(/\{\{unit\}\}/g,   '')
              openWhatsAppWindow(buildWhatsAppUrl(dueDateTemplate.whatsapp, rendered))
              ;(async () => {
                try {
                  const entries = await listEntriesForParty(unitId, dueDateTemplate.id)
                  const dueIn7 = entries.filter(e => {
                    const days = Math.ceil((new Date(e.due_date).getTime() - new Date().getTime()) / 86400000)
                    return days >= 0 && days <= 7 && e.status === 'open'
                  })
                  for (const entry of dueIn7) {
                    await logWhatsAppMessage({ party_id: dueDateTemplate.id, entry_id: entry.id, unit_id: unitId, type: 'duedate_alert', message_content: finalMessage, phone: dueDateTemplate.whatsapp! })
                  }
                } catch (err) { console.error('Failed to log WhatsApp message:', err) }
              })()
              setDueDateTemplate(null); setEditingDueDateTemplate(false); setQuickDueDateParty(null)
            }
          }}
          title={tc('templateModal.titleDueDate')}
        />

        {/* Quick due date preview */}
        <QuickChargeModal
          isOpen={!!quickDueDateParty && !editingDueDateTemplate && dueDateTemplateLoaded}
          onClose={() => { setQuickDueDateParty(null); setDueDateTemplate(null) }}
          party={quickDueDateParty ? { name: quickDueDateParty.name, whatsapp: quickDueDateParty.whatsapp ?? '' } : null}
          message={quickDueDateParty ? renderDueDateMessage(quickDueDateParty) : ''}
          onConfigure={() => { setDueDateTemplate(quickDueDateParty); setEditingDueDateTemplate(true) }}
          onResetTemplate={() => resetDueDateTemplate()}
          onSend={() => {
            if (quickDueDateParty) {
              openWhatsAppWindow(buildWhatsAppUrl(quickDueDateParty.whatsapp!, renderDueDateMessage(quickDueDateParty)))
              ;(async () => {
                try {
                  const entries = await listEntriesForParty(unitId, quickDueDateParty.id)
                  const dueIn7 = entries.filter(e => {
                    const days = Math.ceil((new Date(e.due_date).getTime() - new Date().getTime()) / 86400000)
                    return days >= 0 && days <= 7 && e.status === 'open'
                  })
                  for (const entry of dueIn7) {
                    await logWhatsAppMessage({ party_id: quickDueDateParty.id, entry_id: entry.id, unit_id: unitId, type: 'duedate_alert', message_content: dueDateTemplateStored, phone: quickDueDateParty.whatsapp! })
                  }
                } catch (err) { console.error('Failed to log WhatsApp message:', err) }
              })()
              setQuickDueDateParty(null); setDueDateTemplate(null)
            }
          }}
        />

        {documentParty && (
          <PartyDocumentModal
            party={documentParty}
            userRole={null}
            onClose={() => setDocumentParty(null)}
            onUpdated={(updated: PartyWithStats) => {
              setDocumentParty(updated)
            }}
          />
        )}
      </>
    ),
  }

  return (
    <BaseContactsPage
      namespace="billing.contacts"
      extensions={extensions}
    />
  )
}

// â”€â”€â”€ Inline icon components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function BellIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  )
}

function ChartIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  )
}

function ClipboardIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  )
}
